import { ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ServiceForm } from '../components/ServiceForm';
import { useCreateService, useService, useUpdateService } from '../queries';
import type { ServiceInput } from '../types';

export function ServiceFormPage() {
  const { publicId = '' } = useParams();
  const isEdit = Boolean(publicId);
  const navigate = useNavigate();

  const serviceQuery = useService(isEdit ? publicId : -1);
  const create = useCreateService();
  const update = useUpdateService(publicId);

  async function handleSubmit(values: ServiceInput) {
    const saved = isEdit ? await update.mutateAsync(values) : await create.mutateAsync(values);
    navigate(`/finance/catalog/${saved.public_id}`);
  }

  if (isEdit && serviceQuery.isPending) {
    return (
      <div className="max-w-xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const service = serviceQuery.data;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(isEdit ? `/finance/catalog/${publicId}` : '/finance/catalog')}
        >
          <ArrowLeft />
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">
          {isEdit ? `Edit ${service?.name ?? 'service'}` : 'New service'}
        </h1>
      </div>

      <ServiceForm
        submitLabel={isEdit ? 'Save changes' : 'Create service'}
        defaultValues={
          service
            ? {
                name: service.name,
                category: service.category ? String(service.category) : '',
                description: service.description,
                unit: service.unit,
                base_rate: String(Number(service.base_rate)),
                is_active: service.is_active,
              }
            : undefined
        }
        onSubmit={handleSubmit}
        onCancel={() => navigate(isEdit ? `/finance/catalog/${publicId}` : '/finance/catalog')}
      />
    </div>
  );
}
